"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { hasMinEngagement, isInCooldown, markInstalled, markShownThisSession, recordDismiss, recordPermanentDismiss, wasInstalled, wasShownThisSession } from "@/lib/pwa-install-storage";
type BeforeInstallPromptEvent = Event & {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
};
export type InstallSupport = "native" // beforeinstallprompt fired — Android/desktop Chromium/Edge
| "ios-safari" // manual Add to Home Screen flow
| "unsupported" // Firefox, desktop Safari, in-app webviews, etc.
| "installed" // already running as PWA
| "unknown"; // still detecting

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";
type Ctx = {
  support: InstallSupport;
  isStandalone: boolean;
  isIOS: boolean;
  isInAppBrowser: boolean;
  canPromptNatively: boolean;
  promptInstall: () => Promise<InstallOutcome>;
  dismiss: (permanent?: boolean) => void;
  shouldShowNudge: boolean;
  openedByUser: boolean;
  requestShow: () => void;
};
const PwaInstallContext = createContext<Ctx | null>(null);
function detectStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia?.("(display-mode: standalone)");
  const iosStandalone = (window.navigator as Navigator & {
    standalone?: boolean;
  }).standalone === true;
  return Boolean(mql?.matches) || iosStandalone;
}
function detectIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isiOSDevice = /iPad|iPhone|iPod/.test(ua);
  const isiPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return isiOSDevice || isiPadOS;
}
function detectIOSSafari(): boolean {
  if (!detectIOS()) return false;
  const ua = navigator.userAgent || "";
  // Exclude in-app browsers on iOS (FBAN/FBAV/Instagram/Line/etc.)
  // and Chrome/Firefox on iOS (CriOS/FxiOS — they can't trigger install either).
  if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(ua)) return false;
  if (/FBAN|FBAV|Instagram|Line|Twitter|WhatsApp|Snapchat/.test(ua)) return false;
  return /Safari/.test(ua);
}
function detectInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|Line|Twitter|WhatsApp|Snapchat|MicroMessenger|WeChat/.test(ua);
}
export function PwaInstallProvider(t0) {
  const $ = _c(16);
  const {
    children
  } = t0;
  const deferredPromptRef = useRef(null);
  const [support, setSupport] = useState("unknown");
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [canPromptNatively, setCanPromptNatively] = useState(false);
  const [openedByUser, setOpenedByUser] = useState(false);
  const [cooldownTick, setCooldownTick] = useState(0);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const standalone = detectStandalone();
      const ios = detectIOS();
      const iosSafari = detectIOSSafari();
      const inApp = detectInAppBrowser();
      setIsStandalone(standalone);
      setIsIOS(ios);
      setIsInAppBrowser(inApp);
      if (standalone || wasInstalled()) {
        setSupport("installed");
        return;
      }
      if (inApp) {
        setSupport("unsupported");
        return;
      }
      if (iosSafari) {
        setSupport("ios-safari");
        return;
      }
      const unsupportedFallbackTimer = window.setTimeout(() => {
        setSupport(_temp);
      }, 3000);
      const onBeforeInstall = event => {
        event.preventDefault();
        deferredPromptRef.current = event as BeforeInstallPromptEvent;
        setCanPromptNatively(true);
        setSupport("native");
      };
      const onInstalled = () => {
        deferredPromptRef.current = null;
        setCanPromptNatively(false);
        setIsStandalone(true);
        setOpenedByUser(false);
        setSupport("installed");
        markInstalled();
      };
      const displayModeQuery = window.matchMedia("(display-mode: standalone)");
      const onDisplayModeChange = e => {
        if (e.matches) {
          setIsStandalone(true);
          setSupport("installed");
        }
      };
      window.addEventListener("beforeinstallprompt", onBeforeInstall);
      window.addEventListener("appinstalled", onInstalled);
      displayModeQuery.addEventListener?.("change", onDisplayModeChange);
      return () => {
        window.clearTimeout(unsupportedFallbackTimer);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
        displayModeQuery.removeEventListener?.("change", onDisplayModeChange);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async () => {
      const evt = deferredPromptRef.current;
      if (!evt) {
        return "unavailable";
      }
      try {
        await evt.prompt();
        const choice = await evt.userChoice;
        deferredPromptRef.current = null;
        setCanPromptNatively(false);
        setOpenedByUser(false);
        if (choice.outcome === "accepted") {
          markInstalled();
          setSupport("installed");
          return "accepted";
        }
        recordDismiss();
        setCooldownTick(_temp2);
        return "dismissed";
      } catch {
        setOpenedByUser(false);
        return "unavailable";
      }
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const promptInstall = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = t5 => {
      const permanent = t5 === undefined ? false : t5;
      setOpenedByUser(false);
      if (permanent) {
        recordPermanentDismiss();
      } else {
        recordDismiss();
      }
      markShownThisSession();
      setCooldownTick(_temp3);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const dismiss = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setOpenedByUser(true);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const requestShow = t5;
  let t6;
  bb0: {
    if (openedByUser) {
      t6 = true;
      break bb0;
    }
    if (support === "installed" || support === "unknown") {
      t6 = false;
      break bb0;
    }
    if (support === "unsupported" && !isIOS) {
      t6 = false;
      break bb0;
    }
    if (wasShownThisSession()) {
      t6 = false;
      break bb0;
    }
    if (isInCooldown()) {
      t6 = false;
      break bb0;
    }
    if (!hasMinEngagement()) {
      t6 = false;
      break bb0;
    }
    t6 = true;
  }
  const shouldShowNudge = t6;
  let t7;
  if ($[5] !== canPromptNatively || $[6] !== isIOS || $[7] !== isInAppBrowser || $[8] !== isStandalone || $[9] !== openedByUser || $[10] !== shouldShowNudge || $[11] !== support) {
    t7 = {
      support,
      isStandalone,
      isIOS,
      isInAppBrowser,
      canPromptNatively,
      promptInstall,
      dismiss,
      shouldShowNudge,
      openedByUser,
      requestShow
    };
    $[5] = canPromptNatively;
    $[6] = isIOS;
    $[7] = isInAppBrowser;
    $[8] = isStandalone;
    $[9] = openedByUser;
    $[10] = shouldShowNudge;
    $[11] = support;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const value = t7;
  let t8;
  if ($[13] !== children || $[14] !== value) {
    t8 = <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>;
    $[13] = children;
    $[14] = value;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function _temp3(t_0) {
  return t_0 + 1;
}
function _temp2(t) {
  return t + 1;
}
function _temp(prev) {
  return prev === "unknown" ? "unsupported" : prev;
}
export function usePwaInstall() {
  const ctx = useContext(PwaInstallContext);
  if (!ctx) {
    throw new Error("usePwaInstall must be used within PwaInstallProvider");
  }
  return ctx;
}